import { z } from 'zod/v4';
import { type TStringArrayAsEnum } from '@epihub/core/src/ts';
import { _keyBy } from '@epihub/core/src/fn/array';
import { globallyOnce } from '@epihub/core/src/fn/memoize';
import { type Message, type QuickResponse } from '../types';
import { sfyr } from '../_ctx/sfyr';

export const SupportedLanguages = [
  'it',
  'en',
] as const;
export type SupportedLanguage = typeof SupportedLanguages[number];
export type SupportedLanguages = TStringArrayAsEnum<typeof SupportedLanguages>;
export const zodSupportedLanguage = z.enum(SupportedLanguages);
export const ESupportedLanguages = Object.freeze(
  _keyBy(SupportedLanguages),
) as SupportedLanguages;

export const AvailableThemes = [
  'start',
  'trainer',
  'alacarte',
  'work',
  'projects',
] as const;
export type AvailableTheme = typeof AvailableThemes[number];
export type AvailableThemes = TStringArrayAsEnum<typeof AvailableThemes>;
export const zodAvailableTheme = z.enum(AvailableThemes);
export const EAvailableThemes = Object.freeze(
  _keyBy(AvailableThemes),
) as AvailableThemes;

export function getStaticFirstMessage(lang: 'it' | 'en'): Message {
  return globallyOnce(`firstStaticMessage-${lang}`, () => {
    return {
      id: 'assistant-start',
      role: 'assistant',
      createdAt: new Date().getTime(),
      user: 'Krishan Marco Madan',
      content: sfyr('ui.{lang}.first_intro_message', {
        vars: { lang },
        defaults: {
          'ui.{lang=en}.first_intro_message': `
            Hey! 👋 I'm **Krishan** — entrepreneur, full-stack engineer, and Founder in Residence at **Vento**. 🇮🇹
                                                                                                                                                                                                                                          
            I've worked at Adidas, Timeular, and Close CRM, co-founded a profitable crypto mining & blockchain company, and built AI products with paying customers — ask me anything.                                                            
                                                                                                                                                                                                                                          
            👉 [My career journey](?theme=work&autoMessage=Walk%20me%20through%20your%20career)                                                                                                                                                   
            👉 [What I've built](?theme=projects&autoMessage=What%20are%20your%20most%20interesting%20projects%3F)
            👉 [Get in touch](?calendar=1)
            
            Drop your name in the chat and let's talk.
          `,
          'ui.{lang=it}.first_intro_message': `
            Hey! 👋 Sono **Krishan** — imprenditore, full-stack engineer e Founder in Residence a **Vento**. 🇮🇹
            
            Ho lavorato in Adidas, Timeular e Close CRM, co-fondato un'azienda di crypto mining e blockchain profittevole, e costruito prodotti AI con clienti paganti — chiedimi quello che vuoi.
            
            👉 [Il mio percorso](?theme=work&autoMessage=Raccontami%20il%20tuo%20percorso%20lavorativo)
            👉 [Cosa ho costruito](?theme=projects&autoMessage=Quali%20sono%20i%20tuoi%20progetti%20più%20interessanti%3F)
            👉 [Contattami](?calendar=1)
            
            Scrivimi il tuo nome in chat e parliamo.
          `,
        },
      }),
    };
  });
}

export function getDefaultQuickResponses(lang: 'it' | 'en'): QuickResponse[] {
  return sfyr('ui.{lang}.default_quick_responses', {
    vars: { lang },
    defaults: {
      'ui.{lang=en}.default_quick_responses': [
        {
          emoji: '🗓️',
          content: 'Can I book your calendar?',
        },
        {
          emoji: '🇮🇹',
          content: 'Can we speak in Italian?',
        },
        {
          emoji: '📜',
          content: 'Can I download your CV?',
        },
      ],
      'ui.{lang=it}.default_quick_responses': [
        {
          emoji: '🗓️',
          content: 'Posso fissare un appuntamento?',
        },
        {
          emoji: '🇺🇸',
          content: 'Possiamo parlare in Inglese?',
        },
        {
          emoji: '📜',
          content: 'Posso scaricare il tuo CV?',
        },
      ],
    },
  });
}

export const ASSISTANT_NAME = 'Krishan';
export const DEFAULT_VISITOR_NAME = 'You';
export const CAL_COM_NAME = 'krishanmarco';
